import React from "react";
import { BUSINESS } from "@/lib/business";
import { Link } from "react-router-dom";
import { Lock, ShieldCheck, CreditCard } from "lucide-react";
import { NewsletterSignup } from "@/components/NewsletterSignup";
import { SocialIcon, useSocials } from "@/components/SocialIcons";
import api from "@/lib/api";

const UptimeBadge = () => {
  const [notice, setNotice] = React.useState(null);
  React.useEffect(() => {
    api.get("/public/site-notice").then((r) => setNotice(r.data || {})).catch(() => setNotice({}));
  }, []);
  if (notice === null) return null;
  const ok = !notice.severity;
  return (
    <Link to="/status" className="inline-flex items-center gap-1.5 hover:text-white transition-colors" data-testid="footer-uptime-badge">
      <span className={`w-2 h-2 rounded-full ${ok ? "bg-[#00C853]" : notice.severity === "outage" ? "bg-[#E63946]" : "bg-[#FFC107]"} animate-pulse`} />
      {ok ? "All systems operational" : "Service notice — details"}
    </Link>
  );
};

const SocialRow = () => {
  const socials = useSocials();
  const entries = Object.entries(socials).filter(([, v]) => v);
  if (!entries.length) return null;
  return (
    <div className="flex flex-wrap gap-2.5 mt-4" data-testid="footer-socials">
      {entries.map(([k, url]) => (
        <a key={k} href={url} target="_blank" rel="noopener noreferrer" aria-label={`Follow MRQK UK on ${k}`} title={k.charAt(0).toUpperCase() + k.slice(1)}
          className="w-9 h-9 border border-white/20 flex items-center justify-center hover:border-[#FFC107] hover:text-[#FFC107] transition-colors" data-testid={`footer-social-${k}`}>
          <SocialIcon network={k} className="w-4 h-4" />
        </a>
      ))}
    </div>
  );
};

const BusinessLine = () => {
  const [b, setB] = React.useState(null);
  React.useEffect(() => { api.get("/public/business-details").then((r) => setB(r.data)).catch(() => {}); }, []);
  if (!b?.line) return null;
  return <p className="max-w-7xl mx-auto px-6 pb-4 text-[11px] text-neutral-500 text-center sm:text-left" data-testid="footer-business-line">{b.line}{b.phone ? ` Tel: ${b.phone}.` : ""}</p>;
};

export const Footer = () => (
  <footer className="bg-[#0A0A0A] text-white mt-24" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6 py-16 grid grid-cols-2 md:grid-cols-4 gap-10">
      <div className="col-span-2 md:col-span-1">
        <div className="flex items-center gap-2 mb-4">
          <img src="/logo-full-dark.png" alt="MRQK UK Digital Services Marketplace logo" className="h-10 w-auto object-contain" />
        </div>
        <p className="text-sm text-neutral-400 leading-relaxed">The UK's curated multi-vendor marketplace. Fashion, electronics, groceries & digital goods from trusted sellers.</p>
        <SocialRow />
      </div>
      <div>
        <h4 className="label-caps text-neutral-500 mb-4">Shop</h4>
        <ul className="space-y-2 text-sm text-neutral-300">
          <li><Link to="/products?category=Fashion" className="hover:text-white transition-colors">Fashion</Link></li>
          <li><Link to="/products?category=Electronics" className="hover:text-white transition-colors">Electronics</Link></li>
          <li><Link to="/products?category=Groceries" className="hover:text-white transition-colors">Groceries</Link></li>
          <li><Link to="/products?category=Digital" className="hover:text-white transition-colors">Digital</Link></li>
          <li><Link to="/cards" className="hover:text-white transition-colors" data-testid="footer-cards-link">Greeting cards</Link></li>
          <li><Link to="/gift-vouchers" className="hover:text-white transition-colors" data-testid="footer-vouchers-link">Gift vouchers</Link></li>
          <li><Link to="/pet-portraits" className="hover:text-white transition-colors" data-testid="footer-pets-link">Pet portraits</Link></li>
          <li><Link to="/wall-art" className="hover:text-white transition-colors" data-testid="footer-wall-art-link">Wall art &amp; prints</Link></li>
          <li><Link to="/how-to-print" className="hover:text-white transition-colors" data-testid="footer-how-to-print-link">How to print</Link></li>
          <li><Link to="/brand-studio" className="hover:text-white transition-colors" data-testid="footer-brand-studio-link">Brand Studio (AI logos &amp; kits)</Link></li>
          <li><Link to="/about" className="hover:text-white transition-colors" data-testid="footer-about-link">About MRQK UK</Link></li>
          <li><Link to="/accessibility" className="hover:text-white transition-colors" data-testid="footer-accessibility-link">Accessibility</Link></li>
        </ul>
      </div>
      <div>
        <h4 className="label-caps text-neutral-500 mb-4">Account</h4>
        <ul className="space-y-2 text-sm text-neutral-300">
          <li><Link to="/account" className="hover:text-white transition-colors">My Orders</Link></li>
          <li><Link to="/track" className="hover:text-white transition-colors">Track Order</Link></li>
          <li><Link to="/wishlist" className="hover:text-white transition-colors">Wishlist</Link></li>
          <li><Link to="/sell" className="hover:text-white transition-colors" data-testid="footer-sell-link">Sell with us</Link></li>
          <li><Link to="/restricted-policy" className="hover:text-white transition-colors" data-testid="footer-restricted-link">18+ restricted items policy</Link></li>
          <li><Link to="/hmrc-guide" className="hover:text-white transition-colors" data-testid="footer-hmrc-link">Taxes &amp; HMRC guide</Link></li>
          <li><Link to="/workplace-documents" className="hover:text-white transition-colors" data-testid="footer-documents-link">Workplace documents shop</Link></li>
          <li><Link to="/safety-documents" className="hover:text-white transition-colors" data-testid="footer-safety-link">Risk assessment documents</Link></li>
        </ul>
      </div>
      <div>
        <h4 className="label-caps text-neutral-500 mb-4">Help</h4>
        <ul className="space-y-2 text-sm text-neutral-300 mb-6">
          <li><Link to="/live" className="hover:text-white transition-colors">Live shopping</Link></li>
          <li><Link to="/faq" className="hover:text-white transition-colors">Help Centre / FAQs</Link></li>
          <li><Link to="/returns" className="hover:text-white transition-colors">UK Returns & Refunds</Link></li>
          <li><Link to="/delivery" className="hover:text-white transition-colors">Delivery Information</Link></li>
          <li><Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Privacy Policy</Link></li>
          <li><Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">Terms of Service</Link></li>
          <li><Link to="/responsibilities" className="hover:text-white transition-colors" data-testid="footer-responsibilities-link">Your Responsibilities</Link></li>
          <li><Link to="/contact" className="hover:text-white transition-colors" data-testid="footer-contact-link">Contact us</Link></li>
          <li><Link to="/status" className="hover:text-white transition-colors" data-testid="footer-status-link">System status</Link></li>
          <li><a href="https://buy.stripe.com/bJecN5aIP6R02V3bQub3q00" target="_blank" rel="noreferrer" className="hover:text-white transition-colors" data-testid="footer-payment-link">Pay by secure Stripe link</a></li>
          <li><Link to="/advertise" className="hover:text-white transition-colors" data-testid="footer-advertise-link">Advertise with us</Link></li>
          <li><a href="mailto:Marqueukstore@proton.me" className="hover:text-white transition-colors">Marqueukstore@proton.me</a></li>
        </ul>
        <NewsletterSignup variant="footer" />
      </div>
    </div>
    <div className="border-t border-white/10 py-6 px-6 flex flex-col sm:flex-row items-center justify-center sm:justify-between gap-3 max-w-7xl mx-auto text-xs text-neutral-500">
      <div className="text-center sm:text-left">
        <span>© 2026 MRQK UK Marketplace · Prices include VAT · GBP £</span>
        <address className="not-italic mt-1 text-neutral-500" data-testid="footer-address">{BUSINESS.name} · {BUSINESS.addressOneLine}</address>
      </div>
      <div className="flex items-center gap-4" data-testid="footer-trust">
        <UptimeBadge />
        <span className="inline-flex items-center gap-1.5"><Lock className="w-3.5 h-3.5 text-[#00875A]" strokeWidth={2} />SSL secured</span>
        <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#00875A]" strokeWidth={2} />Buyer protection</span>
        <span className="inline-flex items-center gap-1.5"><CreditCard className="w-3.5 h-3.5 text-[#00875A]" strokeWidth={2} />Stripe · PayPal</span>
      </div>
    </div>
    <BusinessLine />
  </footer>
);
